import { mcn } from "@/utils/mergeClassNames";
import { RefObject, useEffect, useRef } from "react";
import { AnchorLinkItem } from "./AnchorLinks";

type Props = AnchorLinkItem & {
  active: boolean;
};

const useScrollAnchorHorizontally = (
  elementRef: RefObject<HTMLAnchorElement>,
  active: boolean
) => {
  useEffect(() => {
    const element = elementRef.current;
    const parent = elementRef.current?.parentElement;
    if (active && element && parent) {
      const scrollLeft = parent.scrollLeft;
      const scrollRight = parent.scrollLeft + parent.clientWidth;
      const elementPositionLeft = element.offsetLeft;
      const elementPositionRight = element.offsetLeft + element.offsetWidth;

      /*
       * Added shouldScrollIntoView-check to prevent unexpected behaviour
       * where call to scrollIntoView(...) here stopped main page scroll
       * (it only happend when element already was in the view)
       * */
      const shouldScrollIntoView =
        elementPositionLeft < scrollLeft || elementPositionRight > scrollRight;
      if (shouldScrollIntoView) {
        element.scrollIntoView({ block: "nearest", inline: "center" });
      }
    }
  }, [active, elementRef]);
};

export function AnchorLink({ text, active, id }: Props) {
  const elementRef = useRef<HTMLAnchorElement>(null);
  useScrollAnchorHorizontally(elementRef, active);

  const destinationHash = `#${id}`;
  return (
    <a
      ref={elementRef}
      href={destinationHash}
      className={mcn(
        "text-style-body2",
        active ? "text-red-100" : "text-white",
        active && "hover:text-red-100/90",
        "p-3",
        "focus-visible:default-focus"
      )}
      onClick={(e) => {
        /* Custom URL/history manangemnt as "scroll: false" in <Link> / router.push(...)
         * doesn't work for #hash links and we want to controll the scroll manually with
         * behavior: "smooth". NextJS issue: https://github.com/vercel/next.js/issues/36447
         *
         * An alternative approach would be to use "scroll-behavior: smooth" CSS at document
         * level. But that effects all scrolling, and timing of toggling to disable when not
         * wanted is hard.
         */
        if (destinationHash === "#") {
          // If destination is top: Scroll to top and remove hash from URL.
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: "smooth" });
          window.history.pushState(undefined, "", location.pathname);
          return;
        }
        const destination = document.querySelector(destinationHash);
        if (destination) {
          // If destination is found: scroll to element and update URL hash.
          e.preventDefault();
          destination.scrollIntoView({ behavior: "smooth" });
          window.history.pushState(undefined, "", destinationHash);
          return;
        }
      }}
    >
      <span
        className={mcn(
          "border-b-2 border-red-100 hover:border-opacity-100",
          active ? "border-opacity-100" : "border-opacity-0"
        )}
      >
        {text}
      </span>
    </a>
  );
}
